"use client";
import { Box, BoxSx, Checkbox, DropdownOption } from "@modernatx/ui-kit-react-v2";
import React from "react";

export interface DropdownMenuProps {
  dropdownOptions: DropdownOption[];
  showNoResults?: boolean;
  noResultsMessage?: string;
  isOpen: boolean;
  selectedOptions?: Set<DropdownOption["value"]>;
  onOptionSelect: (option: DropdownOption, checked: boolean) => void;
  onDismiss: () => void;
  footer?: React.ReactNode;
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  dropdownOptions,
  showNoResults = false,
  noResultsMessage = "No results found",
  isOpen,
  selectedOptions,
  onOptionSelect,
  onDismiss,
  footer
}) => {
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);

  const optionRefs = React.useRef<HTMLElement[]>([]);
  optionRefs.current = [];

  const menuRef = React.useRef<HTMLDivElement>(null);

  // focus updated on open
  React.useEffect(() => {
    if (isOpen && highlightedIndex >= 0) {
      const option = optionRefs.current[highlightedIndex] as HTMLElement;
      option?.focus();
    }
  }, [highlightedIndex, isOpen]);

  // focus updated when using keyboard nav
  React.useEffect(() => {
    if (isOpen && highlightedIndex === -1) {
      menuRef.current?.focus();
    }
  }, [isOpen, highlightedIndex]);

  if (!isOpen) {
    return null;
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((prev) => (prev < dropdownOptions.length - 1 ? prev + 1 : 0));
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : dropdownOptions.length - 1));
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        if (highlightedIndex !== -1) {
          const option = dropdownOptions[highlightedIndex];

          if (option) {
            const isSelected = selectedOptions?.has(option.value);
            onOptionSelect(option, !isSelected);
          }
        }
        break;
      case "Tab":
      case "Escape":
        event.preventDefault();
        onDismiss();
        break;
      default:
        break;
    }
  };

  const setOptionRef = (el: HTMLElement | null, index: number) => {
    if (el) {
      optionRefs.current[index] = el;
    }
  };

  const dropdownContainerStyles: BoxSx = {
    position: "absolute",
    top: () => "calc(100% + 8px)",
    left: 0,
    width: () => "max(100%, 265px)",
    maxWidth: "100vh",
    overflow: "auto",
    maxHeight: () => "calc(100vh - 200px)",
    border: "1px solid",
    borderColor: "input-stroke-enabled",
    borderRadius: "radius-2",
    backgroundColor: "input-onSurface",
    boxShadow: "shadow-100",
    zIndex: "dropdown",
    transition: "transition-ease-out-medium"
  };

  const dropdownItemContainerStyles: BoxSx = {
    px: "spacing-5",
    py: "spacing-3",
    ":hover": {
      backgroundColor: "surface-brand"
    },
    ":first-child": {
      borderTopLeftRadius: "radius-2",
      borderTopRightRadius: "radius-2"
    },
    ":last-child": {
      borderBottomLeftRadius: "radius-2",
      borderBottomRightRadius: "radius-2"
    },
    cursor: "pointer"
  };

  const dropdownItemStyles: BoxSx = {
    px: "spacing-4",
    py: "spacing-2"
  };

  return (
    <Box
      ref={menuRef}
      sx={dropdownContainerStyles}
      role="listbox"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {dropdownOptions.length > 0 ? (
        dropdownOptions.map((option, index) => {
          const isHighlighted = index === highlightedIndex;
          const isSelected = selectedOptions?.has(option.value);

          return (
            <Box
              key={option.value}
              ref={(el) => setOptionRef(el, index)}
              role="option"
              tabIndex={-1}
              aria-selected={isHighlighted}
              sx={{
                ...dropdownItemContainerStyles,
                ...(isHighlighted && {
                  backgroundColor: "surface-brand"
                })
              }}
              onClick={() => {
                onOptionSelect(option, !isSelected);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <Box
                sx={{
                  ...dropdownItemStyles,
                  pointerEvents: "none"
                }}
              >
                <Checkbox label={option.label} value={isSelected} onChange={() => {}} />
              </Box>
            </Box>
          );
        })
      ) : showNoResults ? (
        <Box role="option" sx={{ padding: "spacing-4", color: "text-incidental" }}>
          {noResultsMessage}
        </Box>
      ) : null}
      {footer && (
        <Box
          sx={{
            borderTop: "1px solid",
            borderColor: "surface-neutral-secondary",
            pt: "spacing-2",
            px: "spacing-4"
          }}
        >
          {footer}
        </Box>
      )}
    </Box>
  );
};
